import { captureException } from "@sentry/react";
import type { QueryClient } from "@tanstack/react-query";
import i18n from "i18next";
import { toast } from "sonner";
import { parseQuestionnaireAccessRequest } from "web_myplann/Api/Nsure/parseQuestionnaireAccessRequest";
import {
	type NsureQuestionnaireAccessRequestDTO,
	type NsureQuestionnaireAccessResponseDTO,
	postNsureQuestionnaireAccessV2,
} from "web_myplann/Infrastructure/Api/Api";
import { processResponse } from "web_myplann/Infrastructure/Api/ApiUtils";
import { handleMyPlannResponseError } from "web_myplann/Infrastructure/handleMyPlannResponseError";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { device, withProcessingOverlay } from "web_myplann/Utils/device";

export type QuestionnaireNotificationPayload = {
	notificationId?: number;
	clientType: unknown;
	identificationNumber: unknown;
};

/**
 * Jednotná reakce na notifikaci dotazníku, ze které nejde sestavit nsure request:
 * Sentry + toast. Volá se až v okamžiku prokliku (ne při renderu), aby jedna
 * vadná položka nespamovala Sentry při každém překreslení. `details` popisuje
 * zdroj (push notifikace / položka timeline) — bez rodného čísla.
 */
export const reportUnusableQuestionnairePayload = (details: string) => {
	captureException(
		new Error(
			`Investment questionnaire ${details} carries an unusable payload`,
		),
	);
	toast.error(i18n.t(Resources.Common.somethingWentWrong));
};

export const getQuestionnaireAccessRequest = (
	payload: QuestionnaireNotificationPayload,
): NsureQuestionnaireAccessRequestDTO | undefined => {
	const requestContract = parseQuestionnaireAccessRequest(payload);

	if (requestContract) {
		return requestContract;
	}

	reportUnusableQuestionnairePayload(
		`notification ${payload.notificationId} (clientType: ${String(payload.clientType)})`,
	);

	return undefined;
};

export const openQuestionnaire = async (
	access: NsureQuestionnaireAccessResponseDTO,
): Promise<boolean> => {
	try {
		const opened = await device.openExternalUrl(access.url, [
			{
				name: "clientZoneExtToken",
				value: access.accessToken,
				domain: new URL(access.url).hostname,
				path: "/",
				secure: true,
			},
		]);

		return opened.payload.success;
	} catch (error) {
		captureException(error);

		return false;
	}
};

/**
 * Vyžádá nsure přístup pro už zvalidovaný request a otevře dotazník v nativním
 * shellu. Validaci payloadu řeší volající — push přes `openQuestionnaireFromPush`,
 * timeline přes `resolveTimelineNavigation`.
 */
export const openQuestionnaireWithAccess = (
	requestContract: NsureQuestionnaireAccessRequestDTO,
	queryClient: QueryClient,
): Promise<void> =>
	withProcessingOverlay(async () => {
		try {
			const access = await processResponse(postNsureQuestionnaireAccessV2)(
				requestContract,
			);

			if (!(await openQuestionnaire(access))) {
				toast.error(i18n.t(Resources.Common.somethingWentWrong));
			}
		} catch (error) {
			handleMyPlannResponseError(error, queryClient);
		}
	});

export const openQuestionnaireFromPush = async (
	payload: QuestionnaireNotificationPayload,
	queryClient: QueryClient,
): Promise<boolean> => {
	const requestContract = getQuestionnaireAccessRequest(payload);

	if (!requestContract) {
		return false;
	}

	await openQuestionnaireWithAccess(requestContract, queryClient);

	return true;
};
